import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  applyActionCode, 
  confirmPasswordReset,
  verifyPasswordResetCode
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { resetPassword } from '../firebase/authService';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert.jsx';
import ActionButton from '../components/common/NewCustomButton';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import { Card, CardHeader, CardContent, CardTitle, CardDescription, CardFooter } from '../components/ui/card';

const AuthActionHandler = () => {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [actionCode, setActionCode] = useState('');
  const [mode, setMode] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Password validation
  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    return '';
  };

  // Determine action type from oobCode by trying different verification methods
  const determineActionTypeFromCode = async (code) => {
    try {
      // Try to verify as password reset code first
      try {
        const email = await verifyPasswordResetCode(auth, code);
        setResetEmail(email);
        setMode('resetPassword');
        setStatus('resetPassword');
        return;
      } catch (resetError) {
        // Not a password reset code, try email verification
        try {
          await applyActionCode(auth, code);
          setMode('verifyEmail');
          setStatus('success');
          setMessage('Email verified successfully! You can now sign in with your email.');
          return;
        } catch (verifyError) {
          // Not an email verification code either
          console.log('Could not determine action type from code');
          setStatus('error');
          setMessage('Invalid action code. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error determining action type:', error);
      setStatus('error');
      setMessage('Invalid action code. Please try again.');
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const actionMode = searchParams.get('mode') || searchParams.get('action');
    const oobCode = searchParams.get('oobCode');
    
    // Map action values to mode values if needed
    let normalizedMode = actionMode;
    if (actionMode === 'reset') normalizedMode = 'resetPassword';
    if (actionMode === 'verify') normalizedMode = 'verifyEmail';
    if (actionMode === 'change') normalizedMode = 'confirmEmailChange';
    
    setMode(normalizedMode);
    setActionCode(oobCode);
    
    // Handle Firebase default auth action URLs
    // Firebase default URLs have format: https://fir-plutoo.firebaseapp.com/__/auth/action?mode=resetPassword&oobCode=...
    if (location.pathname === '/__/auth/action' || location.pathname.includes('__/auth/action')) {
      console.log('Handling Firebase default auth action URL');
    }
    
    // If no mode is specified but we have an oobCode, try to determine the action type
    if (!normalizedMode && oobCode) {
      // We'll determine the action type by trying to verify the code
      determineActionTypeFromCode(oobCode);
    }

    if (!oobCode && normalizedMode === 'resetPassword') {
      // If no action code but we're at reset password, show the email form
      setStatus('requestReset');
      return;
    }

    if (!oobCode) {
      setStatus('error');
      setMessage('Invalid action code. Please try again.');
      return;
    }

    const handleAuthAction = async () => {
      try {
        switch (normalizedMode) {
          case 'verifyEmail':
            await applyActionCode(auth, oobCode);
            setStatus('success');
            setMessage('Email verified successfully! You can now sign in with your email.');
            break;

          case 'resetPassword':
            // Verify the password reset code is valid
            const email = await verifyPasswordResetCode(auth, oobCode);
            setResetEmail(email);
            setStatus('resetPassword');
            break;

          case 'confirmEmailChange':
            await applyActionCode(auth, oobCode);
            setStatus('success');
            setMessage('Email changed successfully! You can now sign in with your new email.');
            break;

          default:
            setStatus('error');
            setMessage('Invalid action. Please try again.');
        }
      } catch (error) {
        console.error('Error handling auth action:', error);
        setStatus('error');
        setMessage(error.message || 'An error occurred. Please try again.');
      }
    };

    if (oobCode) {
      handleAuthAction();
    }
  }, [location]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setPasswordError('');
    
    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      setStatus('success');
      setMessage('Password reset successfully! You can now login with your new password.');
    } catch (error) {
      console.error('Error resetting password:', error);
      setStatus('error');
      setMessage(error.message || 'Failed to reset password. Please try again.');
    }
  };

  const handleRequestPasswordReset = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      setMessage('Please enter your email address');
      return;
    }
    
    try {
      setStatus('processing');
      await resetPassword(resetEmail);
      setStatus('success');
      setMessage('Password reset email sent! Please check your inbox.');
    } catch (error) {
      console.error('Error requesting password reset:', error);
      setStatus('error');
      setMessage(error.message || 'Failed to send reset email. Please try again.');
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'resetPassword':
        return 'Reset Password';
      case 'requestReset':
        return 'Request Password Reset';
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      default:
        return 'Authentication';
    }
  };

  return (
    <div className="container mx-auto max-w-md p-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {getTitle()}
          </CardTitle>
          <CardDescription>
            {status === 'processing' ? 'Processing your request...' : ''}
            {status === 'resetPassword' ? `Enter new password for ${resetEmail}` : ''}
            {status === 'requestReset' ? 'Enter your email to receive a password reset link' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === 'processing' && (
            <Alert>
              <AlertDescription>Please wait while we process your request...</AlertDescription>
            </Alert>
          )}

          {status === 'success' && (
            <Alert>
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          {status === 'error' && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          {status === 'resetPassword' && (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter your new password"
                  required
                  minLength={8}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  required
                  minLength={8}
                />
              </div>
              
              {passwordError && (
                <Alert variant="destructive">
                  <AlertDescription>{passwordError}</AlertDescription>
                </Alert>
              )}
              
              <ActionButton type="submit" fullWidth compact>
                Reset Password
              </ActionButton>
            </form>
          )}
          
          {status === 'requestReset' && (
            <form onSubmit={handleRequestPasswordReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                />
              </div>
              
              <ActionButton type="submit" fullWidth compact>
                Send Reset Link
              </ActionButton>
            </form>
          )}
        </CardContent>
        
        <CardFooter className="flex justify-center">
          {(status === 'success' || status === 'error') && (
            <ActionButton
              variant="outlined"
              onClick={() => navigate('/login')}
              compact
            >
              Return to Login
            </ActionButton>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default AuthActionHandler;
